import { Calendar as CalendarIcon } from 'lucide-react';
import React, { useState } from 'react';
import { Matcher } from 'react-day-picker';

import { Button } from '@/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover';
import { Calendar } from '@/components/ui/inputs/calendar';
import { formatDateForUser } from '@/utils/format';

interface DatePickerProps {
  onSelect: (value: Date) => void;
  selected?: Date;
  isPopoverOpen?: boolean;
  disabledDates?: Matcher | Matcher[];
  onClear?: () => void;
}

export const EstimatedDepartureDatePicker = ({
  selected,
  onSelect,
  isPopoverOpen = false,
  disabledDates,
  onClear,
}: DatePickerProps) => {
  const [month, setMonth] = useState<Date | undefined>(selected);
  const [isOpen, setIsOpen] = useState<boolean>(isPopoverOpen);

  const handleSelect = (value: Date | undefined) => {
    if (!value) return;

    onSelect(value);
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setMonth(selected);
    }
  };

  const handleClear = () => {
    onClear?.();
    setIsOpen(false);
  };

  const formatButtonLabel = () => {
    if (selected) return formatDateForUser(selected);
    return '';
  };

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      <PopoverTrigger asChild>
        <div>
          <Button
            iconLeft={<CalendarIcon size={16} />}
            size="small"
            variant="link"
            data-testid="datepicker-button"
            type="button"
            onClick={() => setIsOpen(true)}
          >
            <span>{formatButtonLabel() || 'Add the estimated departure date'}</span>
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col space-y-2 bg-white p-md">
        <Calendar
          id="date-picker"
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          month={month}
          onMonthChange={setMonth}
          disabled={disabledDates}
        />
        <Button disabled={selected === undefined} className="self-end" size="small" type="button" onClick={handleClear}>
          Clear
        </Button>
      </PopoverContent>
    </Popover>
  );
};
